/**
 * IP Seed 详情抽屉
 * 显示完整 IP 包:logline + 核心冲突 + 主/反 + 爽点 + 0-15s + 剧情快看
 * 注意:分集大纲是编剧工作台的工作,IP 化只产出 300-500 字故事大纲
 */
import { createPortal } from 'react-dom';
import { X, BookOpen, Swords, User, ShieldAlert, Zap, Clock, ScrollText, Wand2 } from 'lucide-react';
import type { TopicCard } from '@starfox/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

interface Props {
  open: boolean;
  topic: TopicCard | null;
  onClose: () => void;
  onKickoff?: (t: TopicCard) => void;
  onIpize?: (t: TopicCard) => void;
  ipizing?: boolean;
}

export function IpSeedDrawer({ open, topic, onClose, onKickoff, onIpize, ipizing }: Props) {
  if (!open || !topic) return null;
  const seed = topic.ipSeed ?? null;
  const banned = !!topic.bannedReason;
  const isIpizing = ipizing ?? !!topic.ipizing;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex">
      <button
        type="button"
        aria-label="close"
        className="flex-1 cursor-default bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <aside className="flex h-full w-full max-w-2xl flex-col overflow-hidden bg-surface shadow-m3">
        <header className="flex items-start justify-between gap-3 border-b border-outline-variant p-5">
          <div className="min-w-0 flex-1">
            {seed?.ipName && (
              <div className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-wide text-brand-500">
                <BookOpen className="h-3.5 w-3.5" />
                {seed.ipName}
              </div>
            )}
            <h2 className="text-lg font-semibold leading-tight text-neutral-800">{topic.title}</h2>
            {seed?.loglinePinned && (
              <p className="mt-2 text-sm leading-relaxed text-neutral-600">{seed.loglinePinned}</p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {(topic.genres ?? []).map((g) => (
                <Badge key={g} variant="info">{g}</Badge>
              ))}
              {(topic.audiences ?? []).map((a) => (
                <Badge key={a} variant="success">{a}</Badge>
              ))}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          {banned && (
            <div className="mb-4 rounded-md bg-danger-soft p-3 text-sm text-danger">
              <div className="font-medium">已屏蔽</div>
              <div className="mt-1 text-xs">{topic.bannedReason}</div>
            </div>
          )}

          {!seed && !banned && (
            <div className="rounded-md bg-surface-variant p-6 text-center text-sm text-neutral-500">
              此条目还没生成完整 IP 包(仅 Top 12 自动 IP 化)
            </div>
          )}

          {seed && (
            <div className="space-y-5">
              <Section icon={<Swords className="h-4 w-4 text-danger" />} title="核心冲突">
                <p className="text-sm leading-relaxed text-neutral-700">{seed.coreConflict}</p>
              </Section>

              <div className="grid grid-cols-2 gap-3">
                <Section icon={<User className="h-4 w-4 text-brand-500" />} title="主角原型">
                  <p className="text-sm text-neutral-700">{seed.mcArchetype}</p>
                </Section>
                <Section icon={<ShieldAlert className="h-4 w-4 text-warning" />} title="反派原型">
                  <p className="text-sm text-neutral-700">{seed.antagonistArchetype}</p>
                </Section>
              </div>

              <Section icon={<Zap className="h-4 w-4 text-warning" />} title="爽点关键词">
                <div className="flex flex-wrap gap-1.5">
                  {seed.cheerPoints.map((c, i) => (
                    <span
                      key={`${c}-${i}`}
                      className="rounded-md bg-warning-soft px-2 py-1 text-xs text-warning"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </Section>

              <Section icon={<Clock className="h-4 w-4 text-info" />} title="0-15s 开场">
                <p className="rounded-md border border-dashed border-outline-variant bg-surface-variant p-3 text-sm leading-relaxed text-neutral-700">
                  {seed.openingScene15s}
                </p>
              </Section>

              {seed.storyline && (
                <Section
                  icon={<ScrollText className="h-4 w-4 text-success" />}
                  title="剧情快看"
                  hint="完整故事走向 · 分集大纲请到编剧工作台展开"
                >
                  <p className="whitespace-pre-line rounded-md bg-surface-variant p-3 text-sm leading-relaxed text-neutral-700">
                    {seed.storyline}
                  </p>
                </Section>
              )}

              <div className="grid grid-cols-3 gap-3 rounded-md bg-surface-variant p-3 text-center">
                <Stat label="适配度" value={`${((topic.dramaSuitability ?? 0) * 100).toFixed(0)}%`} highlight />
                <Stat label="热度" value={(topic.dimensions.heat * 100).toFixed(0)} />
                <Stat label="预估 CPM" value={`¥${seed.estimatedCpm ?? 32}`} />
              </div>
            </div>
          )}
        </div>

        {!banned && (
          <footer className="flex gap-2 border-t border-outline-variant p-4">
            {seed && onIpize && (
              <Button
                variant="secondary"
                onClick={() => onIpize(topic)}
                disabled={isIpizing}
                title="强制重跑 LLM 生成最新 IP 包(覆盖现有 storyline / 爽点等)"
              >
                <Wand2 className={cn('h-4 w-4', isIpizing && 'animate-pulse')} />
                {isIpizing ? '重新生成中…' : '重新生成'}
              </Button>
            )}
            <Button
              variant="agent"
              agentColor="radar"
              className="flex-1"
              onClick={() => onKickoff?.(topic)}
              disabled={isIpizing}
              title={isIpizing ? 'IP 重新生成中,完成后再立项' : undefined}
            >
              {isIpizing ? 'IP 重新生成中…' : '一键立项 · 进入编剧 Agent'}
            </Button>
          </footer>
        )}
      </aside>
    </div>,
    document.body,
  );
}

function Section({
  icon,
  title,
  hint,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-500">
        {icon} {title}
        {hint && (
          <span className="ml-1 text-[10px] font-normal normal-case text-neutral-400">
            · {hint}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-outline">{label}</div>
      <div
        className={cn(
          'mt-0.5 text-base font-semibold tabular-nums',
          highlight ? 'text-brand-500' : 'text-neutral-800',
        )}
      >
        {value}
      </div>
    </div>
  );
}
